import { Box } from "@saleor/macaw-ui-next";
import { Info } from "lucide-react";
import React from "react";
import { useIntl } from "react-intl";

import { analyticsMessages } from "../../messages";
import { Sparkline } from "./Sparkline";
import { surfaceCardStyle } from "./tokens";
import { TrendBadge } from "./TrendBadge";

interface KpiTileV2Props {
  title: string;
  value: string | number;
  subtext?: string;
  delta?: number | null;
  invertDelta?: boolean;
  spark?: number[];
  icon?: React.ReactNode;
  accentColor?: string;
  loading?: boolean;
  na?: boolean;
  info?: string;
  onClick?: () => void;
}

export const KpiTile: React.FC<KpiTileV2Props> = ({
  title,
  value,
  subtext,
  delta,
  invertDelta,
  spark,
  icon,
  accentColor = "#4F46E5",
  loading,
  na,
  info,
  onClick,
}) => {
  const intl = useIntl();
  const [hover, setHover] = React.useState(false);

  return (
    <Box
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onClick}
      padding={5}
      style={{
        ...surfaceCardStyle,
        transition: "transform 160ms ease, box-shadow 160ms ease",
        transform: hover ? "translateY(-1px)" : "translateY(0)",
        boxShadow: hover
          ? "0 8px 24px rgba(15, 23, 42, 0.06), 0 2px 6px rgba(15, 23, 42, 0.04)"
          : surfaceCardStyle.boxShadow,
        position: "relative",
        overflow: "hidden",
        cursor: onClick ? "pointer" : "default",
      }}
    >
      <Box
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: 3,
          background: `linear-gradient(90deg, ${accentColor}, ${accentColor}33)`,
        }}
      />
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Box as="div" fontSize={2} color="default2" style={{ letterSpacing: 0.1 }}>
            {title}
          </Box>
          {info && (
            <Box
              as="span"
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              title={info}
              style={{
                color: "#9CA3AF",
                cursor: "help",
              }}
            >
              <Info size={13} />
            </Box>
          )}
        </Box>
        {icon && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            __width={32}
            __height={32}
            borderRadius={2}
            style={{
              background: `${accentColor}14`,
              color: accentColor,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
        )}
      </Box>

      <Box
        as="div"
        fontSize={8}
        fontWeight="bold"
        marginTop={3}
        style={{
          letterSpacing: "-0.02em",
          lineHeight: 1.1,
          fontFeatureSettings: '"tnum"',
        }}
      >
        {na
          ? intl.formatMessage(analyticsMessages.commonNA)
          : loading
            ? intl.formatMessage(analyticsMessages.commonLoading)
            : value}
      </Box>

      <Box marginTop={3} display="flex" alignItems="center" justifyContent="space-between" gap={3}>
        <Box display="flex" flexDirection="column" gap={1}>
          {delta != null && !na && !loading && (
            <TrendBadge delta={delta} invert={invertDelta} />
          )}
          {subtext && (
            <Box as="div" fontSize={1} color="default2">
              {subtext}
            </Box>
          )}
        </Box>
        {spark && spark.length > 1 && !na && !loading && (
          <Box __width={96} __height={36} style={{ flexShrink: 0 }}>
            <Sparkline data={spark} color={accentColor} />
          </Box>
        )}
      </Box>
    </Box>
  );
};
